import React, { useState, useEffect } from 'react';
import { useModal } from 'hooks/useModal';
import { useRouting } from 'hooks/useRouting';
import { getDividendStorage, setDividendStorage } from 'hooks/useDividend';
import { formatNumber, formatDate, formatDateForString } from 'utils/formatter';
import { convertDate } from 'utils/date';
import { IncomeGainItemProps } from 'pages/DividendDeposit';
import { DividendDepositModal as DividendModal } from 'components/features/modal/DividendDepositModal';
import { yyyymmddToDate } from 'components/features/new-report/daily-report/DailyReport';
import { DividendColumnChart } from './DividendChart';
import { DividendDepositLoading } from './DividendError';

export const getMonthArray = (from_: Date, to_: Date) => {
  const result = [];
  const from =
    yyyymmddToDate(formatDate(convertDate(new Date(from_)), 'yyyyMM') + '01') ??
    convertDate(new Date());
  const to =
    yyyymmddToDate(formatDate(to_, 'yyyyMM') + '01') ?? convertDate(new Date());

  while (from <= to) {
    const year = from.getFullYear();
    const month = ('0' + (from.getMonth() + 1)).slice(-2);
    result.push(`${year}${month}`);
    from.setMonth(from.getMonth() + 1);
  }

  return result;
};

export const DividendDepositModal = () => {
  const [isShowModal, { hideModal, showModal }] = useModal();
  return (
    <>
      <div className="flex flex-row mt-3">
        <div
          className="flex flex-row items-center mr-4 ml-auto text-bluegray-500 rounded-lg cursor-pointer"
          onClick={showModal}>
          <div className="text-sm mr-1">入金データについて</div>
          <div className="icon-info text-bluegray-400"></div>
        </div>
      </div>
      <DividendModal
        {...{
          isShow: isShowModal,
          hide: hideModal,
        }}
      />
    </>
  );
};

const IncomeGainDetailItem = ({
  data,
  targetPeriod,
}: {
  data: incomeGainTransitionDataProps;
  targetPeriod: { from: Date; to: Date };
}) => {
  const routing = useRouting();
  const goDetail = (): void => {
    if (data.total === null) return;

    // memo: yyyyMMdd -> yyyyMM に変換
    const date = yyyymmddToDate(data.date);
    const dateYm = formatDate(date, 'yyyyMM');

    const from = formatDate(targetPeriod.from, 'yyyyMMdd');
    const to = formatDate(targetPeriod.to, 'yyyyMMdd');
    routing.pushRouting(
      {
        path: `/dividenddeposit/detail/${dateYm}/${from}/${to}`,
      },
      true
    );
  };

  return (
    <div
      className={`grid items-center px-4 py-3 border-b border-gray-300 ${
        data.total ? 'cursor-pointer' : ''
      }`}
      style={{ gridTemplateColumns: '1fr 1fr' }}
      onClick={(): void => goDetail()}>
      <div className="ml-2 text-sm">
        {formatDate(yyyymmddToDate(`${data.date}`), 'yyyy/M')}
      </div>
      <div className="flex flex-row items-center ml-auto">
        {data.total ? (
          <>
            <div className="flex flex-col">
              <div className="text-bluegray-700">
                {formatNumber(data.total, {})}
                <span className="ml-1">円</span>
              </div>
            </div>
          </>
        ) : (
          '---'
        )}
        <div className="flex justify-end w-8">
          {data.total ? (
            <div className="icon-chevron-right flex text-2xl text-gray-500"></div>
          ) : (
            <></>
          )}
        </div>
      </div>
    </div>
  );
};

const IncomeGainDetailList = ({
  incomeGainDetailData: data,
  targetPeriod,
}: {
  incomeGainDetailData: incomeGainTransitionDataProps[];
  targetPeriod: { from: Date; to: Date };
}) => {
  const incomeGainDetailData_ = [...data];
  const incomeGainDetailData = incomeGainDetailData_.reverse();
  return (
    <div className="pb-6 text-bluegray-700">
      {incomeGainDetailData.map((data, i) => (
        <IncomeGainDetailItem {...{ data, targetPeriod }} key={i} />
      ))}
    </div>
  );
};

const setSessionStorageDepositChart = (type: number): void => {
  const dividend = getDividendStorage();
  const newDividend = { ...dividend, depositChart: type };
  setDividendStorage(newDividend);
};

export type incomeGainTransitionItemProps = {
  amount: number | null;
  brokerID: string | null;
  brokerName: string | null;
  productType: number | null;
};

export type incomeGainTransitionDataProps = {
  date: string; // yyyymm
  items: incomeGainTransitionItemProps[];
  total: number;
};

export const selectedChartTypes = {
  口座: 1,
  資産種別: 2,
} as const;
export type SelectedChartProps = typeof selectedChartTypes[keyof typeof selectedChartTypes];

// 推移
export const DividendTransition = ({
  incomeGainData,
  targetPeriod,
  updTime,
}: {
  incomeGainData: IncomeGainItemProps[];
  targetPeriod: { from: Date; to: Date };
  updTime: string;
}) => {
  const dividendStorage = getDividendStorage();
  const [selectedChart, setSelectedChart] = useState<SelectedChartProps>(
    dividendStorage === null ||
      !dividendStorage.depositChart ||
      dividendStorage.depositChart === 1
      ? selectedChartTypes.口座
      : selectedChartTypes.資産種別
  );

  const updateSelectedChart = (type: SelectedChartProps): void => {
    setSelectedChart(type);
    setSessionStorageDepositChart(
      type === selectedChartTypes.口座
        ? selectedChartTypes.口座
        : selectedChartTypes.資産種別
    );
  };

  const selectedChartStates = [
    {
      type: selectedChartTypes.口座,
      text: '口座',
    },
    {
      type: selectedChartTypes.資産種別,
      text: '資産種別',
    },
  ];

  const [incomeGainDetailData, setIncomeGainDetailData] = useState<
    incomeGainTransitionDataProps[] | null
  >(null);

  useEffect(() => {
    const periodList = getMonthArray(targetPeriod.from, targetPeriod.to);
    const detailData = periodList
      .map(period => {
        const targetData = incomeGainData.filter(data => {
          const date = yyyymmddToDate(data.date);
          const dateYm = formatDate(date, 'yyyyMM');
          return dateYm === period;
        });

        if (targetData.length === 0) {
          return [
            {
              date: `${period}01`,
              items: [
                {
                  amount: null,
                  brokerID: null,
                  brokerName: null,
                  productType: null,
                },
              ],
              total: null,
            },
          ];
        }

        const amountList = targetData
          .map(data => data.amount)
          .filter(x => x as number) as number[];

        const total =
          amountList === null
            ? null
            : amountList.reduce((acc, current) => acc + current, 0);

        const convertData = {
          date: `${period}01`,
          items: targetData.map(data => {
            return {
              amount: data === undefined ? null : data.amount,
              brokerID: data === undefined ? null : data.brokerID,
              brokerName: data === undefined ? null : data.brokerName,
              productType: data === undefined ? null : data.productType,
            };
          }),
          total,
        };
        return convertData;
      })
      .flat();

    setIncomeGainDetailData(detailData);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetPeriod]);

  if (incomeGainDetailData === null)
    return <DividendDepositLoading str={'日付'} />;

  return (
    <>
      <div className="mt-4 ml-1">
        <DividendColumnChart
          {...{ data: incomeGainDetailData, selectedChart }}
        />
      </div>
      <div className="flex flex-row items-end mt-2">
        <div
          className="relative flex flex-row overflow-hidden w-full items-center rounded border h-8 ml-2 border-bluegray-400"
          style={{ width: '100px' }}>
          <select
            className="w-full h-full appearance-none bg-transparent text-xs pl-3 cursor-pointer"
            value={selectedChart}
            onChange={(e): void => {
              updateSelectedChart(
                Number(e.currentTarget.value) as SelectedChartProps
              );
            }}>
            {selectedChartStates.map((s, i) => {
              return (
                <option key={i} value={s.type}>
                  {s.text}
                </option>
              );
            })}
          </select>
          <div
            className="absolute pointer-events-none flex items-center pr-1"
            style={{ right: '6%' }}>
            <i className="text-bluegray-400 cursor-pointer icon-chevron-down p-0.5 ml-auto" />
          </div>
        </div>
        <div className="ml-auto mr-4 text-xs text-bluegray-500 text-right">
          {formatDateForString(updTime, 'yyyy/MM/dd(ddd) HH:mm')} 更新
        </div>
      </div>
      <DividendDepositModal />
      <div
        className="grid mt-4 px-4 py-1 text-sm text-bluegray-550 font-bold bg-gray-300"
        style={{ gridTemplateColumns: '1fr 1fr' }}>
        <div className="ml-2">日付</div>
        <div>入金額</div>
      </div>
      <IncomeGainDetailList {...{ incomeGainDetailData, targetPeriod }} />
    </>
  );
};
